import { useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Lock, Zap, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Subscription = () => {
  const { isActive, isLoading, subscriptionData } = useSubscription();
  const { hasContactsAccess, hasReservationsAccess, hasCateringAccess, tier } = useFeatureAccess();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleManagePlan = async () => {
    setIsOpeningPortal(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Please log in to manage your subscription');
        return;
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open billing portal. Please try again.');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  // Debug logging
  console.log('Subscription Debug:', {
    tier,
    hasContactsAccess,
    hasReservationsAccess,
    hasCateringAccess,
    subscriptionData
  });

  const getTierLabel = () => {
    if (!tier) return 'No Plan';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading subscription status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <Badge variant={isActive ? "default" : "secondary"} className="mb-4">
            {isActive ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Active Subscription
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-1" />
                Inactive Subscription
              </>
            )}
          </Badge>
          <h1 className="text-4xl font-bold mb-4">
            {isActive ? `Subscription Active` : 'Unlock Premium Features'}
          </h1>
          <p className="text-xl text-muted-foreground">
            {isActive 
              ? `You have full access to all features` 
              : 'Get access to Contacts, Catering & Reservations'}
          </p>
        </div>

        {reason === 'locked' && (
          <Card className="mb-8 border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <Lock className="h-5 w-5" />
                <p className="font-medium">This feature requires an active subscription</p>
              </div>
            </CardContent>
          </Card>
        )}

        {!isActive ? (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-6 w-6 text-primary" />
                Premium Features
              </CardTitle>
              <CardDescription>
                Unlock powerful tools to manage your restaurant business
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Contacts Management</p>
                    <p className="text-sm text-muted-foreground">
                      Track and manage customer inquiries and messages
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Catering Requests</p>
                    <p className="text-sm text-muted-foreground">
                      Handle catering orders with quotes and custom pricing
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Reservation System</p>
                    <p className="text-sm text-muted-foreground">
                      Manage table reservations and booking settings
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4">
                <Button size="lg" className="w-full">
                  Activate Subscription
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Stripe integration coming soon
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Current Plan: {getTierLabel()}</CardTitle>
              <CardDescription>
                {tier === 'basic' && 'Contact Management - Track and manage customer inquiries'}
                {tier === 'premium' && 'Contact Management + Reservation System - Manage bookings and contacts'}
                {tier === 'enterprise' && 'Full Access - Contacts, Reservations, and Catering Management'}
                {!tier && 'No active plan'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Status</span>
                  <Badge variant="default">Active via Stripe</Badge>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Renewal Date</span>
                  <span className="text-sm">
                    {subscriptionData?.subscription?.currentPeriodEnd 
                      ? new Date(subscriptionData.subscription.currentPeriodEnd).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Auto-renew</span>
                  <span className="text-sm">
                    {subscriptionData?.subscription?.cancelAtPeriodEnd ? 'No' : 'Yes'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Contacts Management</span>
                  {hasContactsAccess ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <span className="text-sm font-medium">Reservation System</span>
                  {hasReservationsAccess ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium">Catering Requests</span>
                  {hasCateringAccess ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <X className="h-5 w-5 text-red-600" />
                  )}
                </div>
              </div>
              <div className="pt-6 mt-6 border-t">
                <Button 
                  variant="default" 
                  size="lg"
                  className="w-full" 
                  onClick={handleManagePlan}
                  disabled={isOpeningPortal}
                >
                  {isOpeningPortal ? 'Opening Portal...' : 'Manage Plan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Subscription;
